// @spec GHG-STR-071
// Unit conversion helpers and Climatiq unit type metadata.

/** All unit types supported by the Climatiq API. */
export type ClimatiqUnitType =
  | 'Area'
  | 'AreaOverTime'
  | 'ContainerOverDistance'
  | 'Data'
  | 'DataOverTime'
  | 'Distance'
  | 'DistanceOverTime'
  | 'Energy'
  | 'Money'
  | 'Number'
  | 'NumberOverTime'
  | 'PassengerOverDistance'
  | 'Power'
  | 'Time'
  | 'Volume'
  | 'Weight'
  | 'WeightOverDistance'
  | 'WeightOverTime';

/** Valid values for each unit key. */
export const VALID_UNITS: Record<string, readonly string[]> = {
  area_unit: ['m2', 'km2', 'ft2', 'ha'],
  distance_unit: ['m', 'km', 'ft', 'mi', 'nmi'],
  energy_unit: ['kWh', 'MWh', 'MJ', 'GJ', 'TJ'],
  weight_unit: ['g', 'kg', 't', 'ton', 'lb'],
  volume_unit: ['ml', 'l', 'm3', 'cubic_foot', 'gallon_us', 'bbl'],
  data_unit: ['MB', 'GB', 'TB'],
  time_unit: ['ms', 's', 'min', 'hour', 'day', 'year'],
  power_unit: ['W', 'kW', 'MW'],
  money_unit: ['usd', 'eur', 'gbp', 'cad', 'aud', 'jpy', 'cny', 'chf', 'sek', 'nok', 'dkk', 'pln'],
} as const;

/** Human-readable labels for unit values. */
export const UNIT_LABELS: Record<string, string> = {
  // Area
  m2: 'm\u00B2',
  km2: 'km\u00B2',
  ft2: 'ft\u00B2',
  ha: 'hectares',
  // Distance
  m: 'meters',
  km: 'kilometers',
  ft: 'feet',
  mi: 'miles',
  nmi: 'nautical miles',
  // Energy
  Wh: 'Wh',
  kWh: 'kWh',
  MWh: 'MWh',
  MJ: 'MJ',
  GJ: 'GJ',
  TJ: 'TJ',
  BTU: 'BTU',
  therm: 'therms',
  MMBTU: 'MMBTU',
  // Weight
  g: 'grams',
  kg: 'kilograms',
  t: 'metric tonnes',
  ton: 'US short tons',
  lb: 'pounds',
  // Volume
  ml: 'milliliters',
  l: 'liters',
  m3: 'm\u00B3',
  cubic_foot: 'cubic feet',
  gallon_us: 'US gallons',
  bbl: 'barrels',
  // Data
  MB: 'MB',
  GB: 'GB',
  TB: 'TB',
  // Time
  ms: 'milliseconds',
  s: 'seconds',
  min: 'minutes',
  hour: 'hours',
  day: 'days',
  year: 'years',
  // Power
  W: 'watts',
  kW: 'kilowatts',
  MW: 'megawatts',
  // Money
  usd: 'USD',
  eur: 'EUR',
  gbp: 'GBP',
  cad: 'CAD',
  aud: 'AUD',
  jpy: 'JPY',
  cny: 'CNY',
  chf: 'CHF',
  sek: 'SEK',
  nok: 'NOK',
  dkk: 'DKK',
  pln: 'PLN',
};

/** Common aliases mapped to canonical unit values. */
const UNIT_ALIASES: Record<string, string> = {
  liters: 'l',
  liter: 'l',
  litres: 'l',
  litre: 'l',
  gallons: 'gallon_us',
  gallon: 'gallon_us',
  gal: 'gallon_us',
  'us gallons': 'gallon_us',
  'm^3': 'm3',
  'm³': 'm3',
  'cubic meter': 'm3',
  'cubic meters': 'm3',
  'cubic metre': 'm3',
  'cubic metres': 'm3',
  kilometers: 'km',
  kilometer: 'km',
  kilometres: 'km',
  kilometre: 'km',
  miles: 'mi',
  mile: 'mi',
  meters: 'm',
  meter: 'm',
  metres: 'm',
  metre: 'm',
  tonnes: 't',
  tonne: 't',
  'metric ton': 't',
  'metric tons': 't',
  'metric tonne': 't',
  'metric tonnes': 't',
  kwh: 'kWh',
  mwh: 'MWh',
  mj: 'MJ',
  gj: 'GJ',
  tj: 'TJ',
  btu: 'BTU',
  mmbtu: 'MMBTU',
};

/** Human-readable labels for value parameter keys. */
export const VALUE_KEY_LABELS: Record<string, string> = {
  energy: 'Energy',
  weight: 'Weight',
  distance: 'Distance',
  area: 'Area',
  volume: 'Volume',
  money: 'Money',
  data: 'Data',
  number: 'Number',
  passengers: 'Passengers',
  containers: 'Containers',
  power: 'Power',
};

/** Human-readable labels for unit key parameters. */
export const UNIT_KEY_LABELS: Record<string, string> = {
  energy_unit: 'Energy unit',
  weight_unit: 'Weight unit',
  distance_unit: 'Distance unit',
  area_unit: 'Area unit',
  volume_unit: 'Volume unit',
  money_unit: 'Currency',
  data_unit: 'Data unit',
  time_unit: 'Time unit',
  power_unit: 'Power unit',
};

export type ConversionCategory = 'distance' | 'volume' | 'mass' | 'energy';

const CATEGORY_UNITS: Record<ConversionCategory, readonly string[]> = {
  distance: ['m', 'km', 'ft', 'mi', 'nmi'],
  volume: ['ml', 'l', 'm3', 'cubic_foot', 'gallon_us', 'bbl'],
  mass: ['g', 'kg', 't', 'ton', 'lb'],
  energy: ['kWh', 'MWh', 'MJ', 'GJ', 'TJ'],
};

const NORMALIZED_UNIT_LOOKUP: Record<string, string> = Object.fromEntries(
  Object.values(CATEGORY_UNITS)
    .flat()
    .map((unit) => [unit.toLowerCase(), unit])
);

const UNIT_CATEGORY_LOOKUP: Record<string, ConversionCategory> = Object.fromEntries(
  Object.entries(CATEGORY_UNITS).flatMap(([category, units]) =>
    units.map((unit) => [unit, category as ConversionCategory])
  )
);

/** Description of what a factor's unit_type requires. */
export interface ParameterSpec {
  unitType: string;
  requiredValueKeys: string[];
  requiredUnitKeys: string[];
  validUnits: Record<string, readonly string[]>;
}

/** Which value keys are required for each unit type. */
const UNIT_TYPE_VALUE_KEYS: Record<string, string[]> = {
  Area: ['area'],
  AreaOverTime: ['area'],
  ContainerOverDistance: ['containers', 'distance'],
  Data: ['data'],
  DataOverTime: ['data'],
  Distance: ['distance'],
  DistanceOverTime: ['distance'],
  Energy: ['energy'],
  Money: ['money'],
  Number: ['number'],
  NumberOverTime: ['number'],
  PassengerOverDistance: ['passengers', 'distance'],
  Power: ['power'],
  Time: [],
  Volume: ['volume'],
  Weight: ['weight'],
  WeightOverDistance: ['weight', 'distance'],
  WeightOverTime: ['weight'],
};

/** Which unit keys are required for each unit type. */
const UNIT_TYPE_UNIT_KEYS: Record<string, string[]> = {
  Area: ['area_unit'],
  AreaOverTime: ['area_unit', 'time_unit'],
  ContainerOverDistance: ['distance_unit'],
  Data: ['data_unit'],
  DataOverTime: ['data_unit', 'time_unit'],
  Distance: ['distance_unit'],
  DistanceOverTime: ['distance_unit', 'time_unit'],
  Energy: ['energy_unit'],
  Money: ['money_unit'],
  Number: [],
  NumberOverTime: ['time_unit'],
  PassengerOverDistance: ['distance_unit'],
  Power: ['power_unit'],
  Time: ['time_unit'],
  Volume: ['volume_unit'],
  Weight: ['weight_unit'],
  WeightOverDistance: ['distance_unit', 'weight_unit'],
  WeightOverTime: ['time_unit', 'weight_unit'],
};

const PREFERRED_UNITS: Record<string, readonly string[]> = {
  area_unit: ['m2', 'ha', 'km2', 'ft2'],
  distance_unit: ['km', 'mi', 'm'],
  energy_unit: ['kWh', 'MWh', 'MJ', 'GJ', 'TJ'],
  weight_unit: ['kg', 't', 'lb', 'ton', 'g'],
  volume_unit: ['l', 'm3', 'gallon_us'],
  data_unit: ['GB', 'TB', 'MB'],
  time_unit: ['hour', 'day', 'year', 'min', 's'],
  power_unit: ['kW', 'MW', 'W'],
  money_unit: ['usd', 'eur', 'gbp'],
};

/**
 * Build a parameter spec from a unit type string.
 * This allows the frontend to render the correct form fields
 * without needing to call the backend.
 */
export function getParameterSpec(unitType: string): ParameterSpec {
  const requiredValueKeys = UNIT_TYPE_VALUE_KEYS[unitType] ?? [];
  const requiredUnitKeys = UNIT_TYPE_UNIT_KEYS[unitType] ?? [];
  const validUnits: Record<string, readonly string[]> = {};
  for (const key of requiredUnitKeys) {
    const unitValues = VALID_UNITS[key];
    if (unitValues) {
      validUnits[key] = unitValues;
    }
  }
  return { unitType, requiredValueKeys, requiredUnitKeys, validUnits };
}

export function normalizeUnit(unit: string): string {
  const trimmed = unit.trim();
  if (!trimmed) return '';
  const alias = UNIT_ALIASES[trimmed.toLowerCase()];
  const resolved = alias ?? trimmed;
  return NORMALIZED_UNIT_LOOKUP[resolved.toLowerCase()] ?? resolved;
}

export function getUnitLabel(unit: string): string {
  const normalized = normalizeUnit(unit);
  return UNIT_LABELS[normalized] ?? normalized;
}

function normalizeUnitToken(unit: string): string | null {
  const normalized = normalizeUnit(unit);
  if (!normalized) return null;
  return NORMALIZED_UNIT_LOOKUP[normalized.toLowerCase()] ?? normalized;
}

export function getActivityUnitFromFactorUnit(rawUnit: string): string {
  const compact = rawUnit.replace(/\s+/g, '');
  if (!compact) return rawUnit;
  const withoutPrefix = compact.toLowerCase().startsWith('kg/')
    ? compact.slice(3)
    : compact.includes('/')
      ? (compact.split('/', 2)[1] ?? compact)
      : compact;
  return normalizeUnitToken(withoutPrefix) ?? withoutPrefix;
}

export function toDisplayActivityUnit(rawUnit: string): string {
  return getActivityUnitFromFactorUnit(rawUnit);
}

export function getUnitCategory(unit: string): ConversionCategory | null {
  const normalized = normalizeUnitToken(unit);
  if (!normalized) return null;
  return UNIT_CATEGORY_LOOKUP[normalized] ?? null;
}

export function getCompatibleUnits(unit: string): string[] {
  const category = getUnitCategory(unit);
  if (!category) return [];
  return [...CATEGORY_UNITS[category]];
}

// Conversion factors: [from_unit, to_unit] -> multiplier
const CONVERSIONS: Record<string, number> = {
  'mi->km': 1.60934,
  'm->km': 0.001,
  'ft->m': 0.3048,
  'nmi->km': 1.852,
  'lb->kg': 0.453592,
  'ton->t': 0.907185,
  't->kg': 1000.0,
  'g->kg': 0.001,
  'gallon_us->l': 3.78541,
  'l->m3': 0.001,
  'cubic_foot->m3': 0.0283168,
  'ml->l': 0.001,
  'bbl->l': 158.987,
  'BTU->kWh': 0.000293071,
  'therm->kWh': 29.3071,
  'MMBTU->kWh': 293.071,
  'MJ->kWh': 0.277778,
  'GJ->kWh': 277.778,
  'TJ->kWh': 277778.0,
  'Wh->kWh': 0.001,
  'MWh->kWh': 1000.0,
  'ft2->m2': 0.092903,
  'ha->m2': 10000.0,
  'km2->m2': 1000000.0,
  'MB->GB': 0.001,
  'TB->GB': 1000.0,
  'W->kW': 0.001,
  'MW->kW': 1000.0,
};

interface QueueItem {
  unit: string;
  factor: number;
}

function findConversionFactor(fromUnit: string, toUnit: string): number | null {
  if (fromUnit === toUnit) return 1;

  const queue: QueueItem[] = [{ unit: fromUnit, factor: 1 }];
  const visited = new Set<string>([fromUnit]);

  while (queue.length > 0) {
    const current = queue.shift();
    if (!current) break;

    for (const [edge, edgeFactor] of Object.entries(CONVERSIONS)) {
      const [from, to] = edge.split('->');
      if (!from || !to) continue;

      if (from === current.unit && !visited.has(to)) {
        const nextFactor = current.factor * edgeFactor;
        if (to === toUnit) return nextFactor;
        visited.add(to);
        queue.push({ unit: to, factor: nextFactor });
      }
      if (to === current.unit && !visited.has(from)) {
        const nextFactor = current.factor / edgeFactor;
        if (from === toUnit) return nextFactor;
        visited.add(from);
        queue.push({ unit: from, factor: nextFactor });
      }
    }
  }

  return null;
}

/**
 * Convert a value between units. Returns null if no conversion is available.
 */
export function convertUnit(
  value: number,
  fromUnit: string,
  toUnit: string
): number | null {
  const normalizedFrom = normalizeUnit(fromUnit);
  const normalizedTo = normalizeUnit(toUnit);
  const factor = findConversionFactor(normalizedFrom, normalizedTo);
  if (factor === null) return null;
  return value * factor;
}

/**
 * Build a constrained list of compatible units for an emission factor.
 */
export function getCompatibleUnitsForFactor(input: {
  factorUnit: string;
  unitType?: string;
}): string[] {
  const normalizedFactorUnit = getActivityUnitFromFactorUnit(input.factorUnit);
  if (!normalizedFactorUnit) return [];

  const requiredKeys = input.unitType ? UNIT_TYPE_UNIT_KEYS[input.unitType] ?? [] : [];
  const primaryKeys = requiredKeys.filter((key) => key !== 'time_unit');
  if (primaryKeys.length !== 1) {
    return [normalizedFactorUnit];
  }

  const primaryKey = primaryKeys[0];
  if (!primaryKey) return [normalizedFactorUnit];
  const validUnits = VALID_UNITS[primaryKey];
  if (!validUnits?.includes(normalizedFactorUnit)) {
    return [normalizedFactorUnit];
  }

  const preferredUnits = PREFERRED_UNITS[primaryKey] ?? validUnits;
  const ordered = [normalizedFactorUnit];
  for (const candidate of preferredUnits) {
    if (!validUnits.includes(candidate)) continue;
    if (candidate === normalizedFactorUnit) continue;
    if (convertUnit(1, candidate, normalizedFactorUnit) === null) continue;
    ordered.push(candidate);
  }
  return ordered;
}

/**
 * Format a number for display with appropriate precision.
 */
export function formatEmissionValue(value: number, unit = 'kg CO2e'): string {
  if (value >= 1000) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 1 })} ${unit}`;
  }
  if (value >= 1) {
    return `${value.toLocaleString(undefined, { maximumFractionDigits: 3 })} ${unit}`;
  }
  return `${value.toLocaleString(undefined, { maximumFractionDigits: 6 })} ${unit}`;
}
